import React, {useEffect, useRef, useState} from 'react';
import {GenomeInterface} from "../../../services/interface/query/genome-interface";
import PersonGenome from "./PersonGenome";
import {useTranslation} from "react-i18next";
import {SexEnum} from "../../../services/enum/sexEnum";
import LinesVertical from "./LinesVertical";
import LinesHorizontal from "./LinesHorizontal";

const styleTitularPerson = {color: 'bg-orange-400 text-header'}
const styleSpousePerson = {color: 'bg-orange-400 text-header'}
const styleFatherPerson = {color: 'bg-gray-400 text-header'}
const styleBrotherPerson = {color: 'bg-orange-300 text-header'}
const styleSonPerson = {color: 'bg-gray-300 text-header'}
const styleSonSpousePerson = {color: 'bg-gray-400 text-header'}

interface Props {
    person?: GenomeInterface;
    setValue?: React.Dispatch<React.SetStateAction<number>>;
}

export function TreeGenome({person, setValue}: Props) {
    const {t} = useTranslation("global") as { t: (key: string) => string };
    const [personRefList, setPersonRefList] = useState<any[]>([])
    const [linesVertical, setLinesVertical] = useState<any[]>([])
    const [linesHorizontal, setLinesHorizontal] = useState<any[]>([])
    const containerRef = useRef<HTMLDivElement>(null);
    const genomeRef = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState<string | undefined>()

    function pushLineVertical(identification: string | undefined, person1: any, person2: any, parent: string) {
        if (person2 && person1) {
            const line = {
                identification: identification,
                person1: {x: person1.x, y: person1.y, height: person1.height, width: person1.width},
                person2: {x: person2.x, y: person2.y, height: person2.height, width: person2.width},
                relationship: parent,
                entry: person2.entry,
            }

            const index = linesVertical.findIndex(item => item.identification === identification && item.relationship === parent);
            if (index === -1) {
                setLinesVertical(prevList => [...prevList, line]);
            } else {
                setLinesVertical(prevList => {
                    const newList = [...prevList];
                    newList[index] = line;
                    return newList;
                });
            }
        }
    }

    function pushLineHorizontal(identification: string | undefined, person1: any, person2: any, parent: string, deactivateHover: boolean | undefined, color: string | undefined) {
        if (person2 && person1) {
            const line = {
                identification: identification,
                person1: {x: person1.x, y: person1.y, height: person1.height, width: person1.width},
                person2: {x: person2.x, y: person2.y, height: person2.height, width: person2.width},
                relationship: parent,
                entry: person2.entry,
                deactivateHover: deactivateHover,
                color: color
            }

            const index = linesHorizontal.findIndex(item => item.identification === identification && item.relationship === parent);
            if (index === -1) {
                setLinesHorizontal(prevList => [...prevList, line]);
            } else {
                setLinesHorizontal(prevList => {
                    const newList = [...prevList];
                    newList[index] = line;
                    return newList;
                });
            }
        }
    }

    function getPersonByRelation(relationship: string) {
        return personRefList.find(item => item.relationship === relationship)
    }

    useEffect(() => {
        if (personRefList) {
            const personGraph = getPersonByRelation(t('titular'))
            const personFatherGraph = getPersonByRelation(t('father'))
            const personMotherGraph = getPersonByRelation(t('mother'))
            const personSpouseGraph = getPersonByRelation(t('spouse'))
            if (personGraph) {
                pushLineVertical(person?.personFather?.identification, personGraph, personFatherGraph, t('father'))
                pushLineVertical(person?.personMother?.identification, personGraph, personMotherGraph, t('mother'))
                pushLineHorizontal(person?.personMother?.identification, personGraph, personSpouseGraph, t('spouse'), true, '#9ca3af')
                if (person?.siblingsPersons && person.siblingsPersons.length > 0) {
                    person.siblingsPersons.forEach((item, index) => {
                        const relation = item.sexCod === SexEnum.HOMBRE ? `${t('brother')} ${index + 1}` : `${t('sister')} ${index + 1}`
                        const personBrotherGraph = getPersonByRelation(relation)
                        pushLineHorizontal(item.identification, personGraph, personBrotherGraph,
                            relation, false, undefined)
                        if (personFatherGraph && item.identificationFather === personFatherGraph.identification) {
                            pushLineHorizontal(`${item.identification}_${relation}_${t('father')}`, personFatherGraph, personBrotherGraph,
                                `${t('father')}`, false, undefined)
                        }
                        if (personMotherGraph && item.identificationMother === personMotherGraph.identification) {
                            pushLineHorizontal(`${item.identification}_${relation}_${t('mother')}`, personMotherGraph, personBrotherGraph,
                                `${t('mother')}`, false, undefined)
                        }
                    })
                }
                if (person?.childrenPersons && person.childrenPersons.length > 0) {
                    person.childrenPersons.forEach((item, index) => {
                        const relation = item.sexCod === SexEnum.HOMBRE ? `${t('son')}` : `${t('daughter')}`
                        const uniqueRelation = `${relation} ${index + 1}`
                        pushLineVertical(
                            `${item.identification}_${uniqueRelation}`,
                            personGraph,
                            getPersonByRelation(uniqueRelation),
                            relation
                        )
                    })
                }
                if (person?.spousePerson?.childrenPersons && person.spousePerson.childrenPersons.length > 0) {
                    person.spousePerson.childrenPersons.forEach((item, index) => {
                        const relation = item.codSexo === SexEnum.HOMBRE ? `${t('son')}` : `${t('daughter')}`
                        const uniqueRelation = `${relation} ${t('spouse')} ${index + 1}`
                        pushLineVertical(
                            `${item.cedula}_${uniqueRelation}`,
                            personSpouseGraph,
                            getPersonByRelation(uniqueRelation),
                            relation
                        )
                    })
                }
                getWidth()
            }
        }
    }, [personRefList]);

    function renderBrothers() {
        if (person && person.siblingsPersons && person.siblingsPersons.length > 0) {
            const middle = (person.siblingsPersons.length / 2).toFixed(1);
            return (
                <div className={`w-full flex flex-row gap-6 mt-[80px]`}>
                    {person.siblingsPersons.map((item, index) => {
                            const aux = (middle === index.toFixed(1).toString() || middle === (index + 0.5).toFixed(1).toString())
                            return (
                                <div key={`${index}-brothers`}
                                     className={`flex flex-row gap-6 ${index === 0 ? 'ml-auto' : ''} ${(index + 1) === person?.siblingsPersons?.length ? 'mr-auto' : ''}`}>
                                    {aux && (person?.spousePerson === undefined || person?.spousePerson === null) && renderTitular()}
                                    <div key={index}>
                                        <PersonGenome setValue={setValue} style={styleBrotherPerson}
                                                      relationship={item.sexCod === SexEnum.HOMBRE ? `${t('brother')} ${(index + 1)}` : `${t('sister')} ${(index + 1)}`}
                                                      person={item}
                                                      {...{
                                                          personRefList,
                                                          setPersonRefList,
                                                          genomeRef,
                                                          containerRef,
                                                          width
                                                      }}/>
                                    </div>
                                    {aux && person?.spousePerson && renderTitular()}
                                </div>)
                        }
                    )
                    }
                </div>
            )
        }
    }

    function renderTitular() {
        return (
            <div className={'flex flex-row gap-6 ml-[30px] mr-[30px]'}>
                <PersonGenome setValue={setValue} style={styleTitularPerson} relationship={t('titular')}
                              person={person} {...{personRefList, setPersonRefList, genomeRef, containerRef, width}}/>
                {person?.spousePerson !== undefined && person?.spousePerson !== null &&
                    <PersonGenome
                        setValue={setValue}
                        style={styleSpousePerson}
                        relationship={t('spouse')}
                        person={person?.spousePerson}
                        {...{
                            personRefList,
                            setPersonRefList,
                            genomeRef,
                            containerRef,
                            width
                        }}
                    />}
            </div>
        )
    }

    function renderSons() {
        if (!person?.childrenPersons || (person.childrenPersons && person.childrenPersons.length === 0)) {
            return <></>
        }

        return (
            <div
                className={`${person?.childrenPersons && person.childrenPersons.length > 4 ? 'grid grid-rows-2 grid-flow-col' : 'flex flex-row'} gap-6 ml-auto mr-auto`}>
                {person?.childrenPersons && person.childrenPersons.map((item, index) =>
                    <PersonGenome setValue={setValue} style={styleSonPerson} key={index}
                                  relationship={item.sexCod === SexEnum.HOMBRE ?
                                      `${t('son')} ${(index + 1)}` : `${t('daughter')} ${(index + 1)}`}
                                  person={item}
                                  {...{
                                      personRefList,
                                      setPersonRefList,
                                      genomeRef,
                                      containerRef,
                                      width
                                  }}/>
                )}
            </div>
        )
    }

    function renderSonsSpouse() {
        if (!person?.spousePerson?.childrenPersons || (person?.spousePerson?.childrenPersons && person?.spousePerson?.childrenPersons.length === 0)) {
            return <></>
        }

        return (
            <div
                className={`${person?.spousePerson?.childrenPersons && person?.spousePerson?.childrenPersons.length > 4 ? 'grid grid-rows-2 grid-flow-col' : 'flex flex-row'} gap-6 ml-auto mr-auto`}>
                {person?.spousePerson?.childrenPersons && person?.spousePerson?.childrenPersons.map((item, index) =>
                    <PersonGenome setValue={setValue} style={styleSonSpousePerson} key={index}
                                  relationship={item.codSexo === SexEnum.HOMBRE ?
                                      `${t('son')} ${t('spouse')} ${(index + 1)}` : `${t('daughter')} ${t('spouse')} ${(index + 1)}`}
                                  person={item}
                                  {...{
                                      personRefList,
                                      setPersonRefList,
                                      genomeRef,
                                      containerRef,
                                      width
                                  }}/>
                )}
            </div>
        )
    }

    useEffect(() => {
        if (containerRef !== undefined && containerRef.current !== undefined && containerRef?.current?.scrollWidth !== undefined) {
            setWidth(`${containerRef?.current?.scrollWidth}px`)
        }
    }, [containerRef]);

    function getWidth() {
        if (containerRef && containerRef.current) {
            return containerRef.current.scrollWidth;
        }
    }

    return (
        <>
            <div className={'!border border-secondary rounded-[5px] px-2'}>
                {person !== undefined &&
                    <div ref={containerRef} className={'w-full h-full flex flex-col p-2 overflow-y-scroll pb-5'}>
                        <div id={'genome-container'} ref={genomeRef}
                             className={`flex flex-col items-center relative`}
                             style={{minWidth: width}}
                        >
                            <div className={`flex flex-row gap-6 w-full justify-center`}>
                                {person?.personFather &&
                                    <PersonGenome setValue={setValue} style={styleFatherPerson}
                                                  relationship={t('father')}
                                                  person={person?.personFather} {...{
                                        personRefList,
                                        setPersonRefList,
                                        genomeRef,
                                        containerRef,
                                        width
                                    }}/>}
                                {person?.personMother &&
                                    <PersonGenome setValue={setValue} style={styleFatherPerson}
                                                  relationship={t('mother')}
                                                  person={person?.personMother} {...{
                                        personRefList,
                                        setPersonRefList,
                                        genomeRef,
                                        containerRef,
                                        width
                                    }}/>}
                            </div>
                            {renderBrothers()}
                            {(!person?.siblingsPersons || (person.siblingsPersons && person.siblingsPersons.length === 0))
                                && <div className={`flex flex-row gap-6 mt-[80px]`}>
                                    {renderTitular()}
                                </div>}
                            <div className={'w-full flex flex-row gap-6 mt-[80px]'}>
                                {renderSons()}
                                {renderSonsSpouse()}
                            </div>
                            {<LinesVertical {...{linesVertical, t}}/>}
                            {<LinesHorizontal {...{linesHorizontal, t}}/>}
                        </div>
                    </div>}
            </div>
        </>
    );
}
